import React, { useCallback, useEffect, useMemo, useRef, } from 'react';
import { Menu, MenuItem, Tooltip } from '@doxyme/ui';
import { openCallSettingsFromMenu } from '../../actions';
import { useDispatch, useSelector } from 'react-redux';
import { getIsCaptionsOpen, toggleClosedCaptionsOpen } from '../../../captions';
import { openBackgroundEffectsModal } from '../../../core/actions';
import * as troubleshooting from '../../../troubleshooting';
import { useIntl } from 'react-intl';
import { noop } from 'lodash';
import { utils, hasGrowthBookFeature, GrowthBookFeatures, canSeeBackgroundEffects, } from '../../../core';
import './CallMenu.scss';
const translationsKeyMap = {
    settings: 'call.actions.options.items.settings',
    troubleshooting: 'call.actions.options.items.troubleshooting',
    backgroundEffects: 'backgroundEffects.title',
    backgroundEffectsTooltip: 'backgroundEffects.tooltip',
    closedCaptions: 'call.actions.options.items.closedCaptions',
};
const AccessibleMenuItem = ({ text, icon, onSelect, ariaLabel, disabled = false, }) => {
    const activate = () => {
        if (disabled) {
            return;
        }
        onSelect();
    };
    const handleKeyDown = (e) => {
        if (disabled) {
            return;
        }
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelect();
        }
    };
    return (<div className="call-menu__item" role="menuitem" tabIndex={disabled ? -1 : 0} aria-label={ariaLabel} aria-disabled={disabled} onClick={activate} onKeyDown={handleKeyDown}>
      <MenuItem text={text} icon={icon} disabled={disabled}/>
    </div>);
};
export const CallMenu = ({ isPatient, toggleMenu = noop, }) => {
    const dispatch = useDispatch();
    const intl = useIntl();
    const containerRef = useRef(null);
    const localized = useMemo(() => utils.formatObjectValues(translationsKeyMap, (value) => intl.formatMessage({ id: value })), []);
    const isBackgroundEffectsEnabled = useMemo(() => canSeeBackgroundEffects(), []);
    const isClosedCaptionsOpen = useSelector(getIsCaptionsOpen);
    const onBackgroundEffects = useCallback(() => {
        toggleMenu(false);
        dispatch(openBackgroundEffectsModal('call menu'));
    }, []);
    const onTroubleshooting = useCallback(() => {
        dispatch(troubleshooting.actions.openTroubleshooting());
    }, []);
    const onClosedCaptions = () => {
        toggleMenu(false);
        dispatch(toggleClosedCaptionsOpen(!isClosedCaptionsOpen, isPatient));
    };
    const onCallSettings = useCallback(() => {
        toggleMenu(false);
        dispatch(openCallSettingsFromMenu(isPatient));
    }, [isPatient]);
    useEffect(() => {
        const firstItem = containerRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])');
        firstItem?.focus();
    }, []);
    const onContainerKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.preventDefault();
            e.stopPropagation();
            toggleMenu(false);
        }
    };
    return (<div ref={containerRef} className="call-menu__items" role="menu" onKeyDown={onContainerKeyDown}>
      <Menu>
        {isBackgroundEffectsEnabled && (<Tooltip size="medium" theme="light" pointerPosition="top-center" positionShift={8} isInteractive={false} borderRadius={16} isDefaultTriggerPadding={false} content={localized.backgroundEffectsTooltip}>
            <AccessibleMenuItem text={localized.backgroundEffects} icon="visual-effects" onSelect={onBackgroundEffects} ariaLabel={localized.backgroundEffects}/>
          </Tooltip>)}
        {hasGrowthBookFeature(GrowthBookFeatures.ClosedCaptions) && (<AccessibleMenuItem text={localized.closedCaptions} icon="closed-captions" onSelect={onClosedCaptions} ariaLabel={localized.closedCaptions} disabled={isClosedCaptionsOpen}/>)}
        <AccessibleMenuItem text={localized.troubleshooting} icon="troubleshooting" onSelect={onTroubleshooting} ariaLabel={localized.troubleshooting}/>
        <AccessibleMenuItem text={localized.settings} icon="call-settings" onSelect={onCallSettings} ariaLabel={localized.settings}/>
      </Menu>
    </div>);
};
